import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { BRAND, errMsg, inr } from "./constants";

const PERIODS = [["day", "Day"], ["week", "Week"], ["month", "Month"]];
const SRC = { walk_in: "Walk-in", inquiry: "Inquiry", ota: "OTA", guest_request: "Public page", flatmate: "Flatmate", owner: "Owner added" };

function Stat({ tid, label, value, accent }) {
  return (
    <div data-testid={tid} className="rounded-2xl border-2 bg-white p-3" style={{ borderColor: BRAND.line }}>
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: BRAND.muted }}>{label}</div>
      <div className="text-xl font-bold mt-0.5" style={{ color: accent || BRAND.green }}>{value}</div>
    </div>
  );
}

function Chips({ title, data, tid }) {
  const entries = Object.entries(data || {});
  return (
    <div data-testid={tid} className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
      <div className="text-sm font-bold" style={{ color: BRAND.green }}>{title}</div>
      {entries.length === 0 && <div className="text-xs mt-2" style={{ color: BRAND.muted }}>Nothing in this range.</div>}
      <div className="mt-2 flex flex-wrap gap-2">
        {entries.map(([k, v]) => <span key={k} className="chip text-xs">{SRC[k] || k} · <b>{v}</b></span>)}
      </div>
    </div>
  );
}

function RangeBar({ period, setPeriod, range, setRange, onCsv }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="inline-flex rounded-full border-2 p-1 bg-white" style={{ borderColor: BRAND.line }}>
        {PERIODS.map(([k, l]) => (
          <button key={k} data-testid={`report-period-${k}`} onClick={() => { setPeriod(k); setRange({ start: "", end: "" }); }}
            className="px-4 py-1.5 rounded-full text-xs font-bold transition-colors" style={{ background: period === k ? BRAND.green : "transparent", color: period === k ? "#fff" : BRAND.green }}>{l}</button>
        ))}
      </div>
      <input type="date" data-testid="report-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="rounded-xl border-2 px-2 py-1.5 text-xs" />
      <span className="text-xs" style={{ color: BRAND.muted }}>to</span>
      <input type="date" data-testid="report-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="rounded-xl border-2 px-2 py-1.5 text-xs" />
      <button data-testid="report-csv-btn" onClick={onCsv} className="rounded-full px-4 py-2 text-xs font-bold text-white inline-flex items-center gap-1.5" style={{ background: BRAND.orange }}><Download className="w-3.5 h-3.5" /> Download CSV</button>
    </div>
  );
}

export function ReportsPanel({ pid, authFetch, API }) {
  const [period, setPeriod] = useState("day");
  const [range, setRange] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const qs = (fmt) => `period=${period}${range.start ? `&start=${range.start}` : ""}${range.end ? `&end=${range.end}` : ""}&fmt=${fmt}`;
  const load = useCallback(() => authFetch({ url: `${API}/homestay/properties/${pid}/reports?${qs("json")}` })
    .then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e, "Could not load report"))), [pid, authFetch, API, period, range]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const csv = async () => {
    try {
      const r = await authFetch({ url: `${API}/homestay/properties/${pid}/reports?${qs("csv")}`, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `report-${period}-${d?.start}-to-${d?.end}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("CSV download failed"); }
  };

  const t = d?.totals || {};
  return (
    <div data-testid="reports-panel" className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-lg font-bold" style={{ color: BRAND.green }}>Revenue & occupancy reports</div>
          <div className="text-xs" style={{ color: BRAND.muted }} data-testid="report-range-label">{d ? `${d.start} → ${d.end} · ${d.total_beds} beds` : "Loading…"}</div>
        </div>
        <RangeBar period={period} setPeriod={setPeriod} range={range} setRange={setRange} onCsv={csv} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <Stat tid="report-revenue" label="Revenue" value={inr(t.revenue_inr)} />
        <Stat tid="report-occupancy" label="Occupancy" value={`${t.occupancy_pct ?? 0}%`} accent={BRAND.orange} />
        <Stat tid="report-bookings" label="Bookings" value={t.bookings ?? 0} />
        <Stat tid="report-commission" label="Commission" value={inr(t.commission_inr)} />
        <Stat tid="report-cgst" label="CGST" value={inr(t.cgst_inr)} />
        <Stat tid="report-sgst" label="SGST" value={inr(t.sgst_inr)} />
        <Stat tid="report-igst" label="IGST" value={inr(t.igst_inr)} />
      </div>
      <div data-testid="report-chart" className="rounded-2xl border-2 bg-white p-4" style={{ borderColor: BRAND.line }}>
        <div className="text-sm font-bold mb-2" style={{ color: BRAND.green }}>Revenue by {period}</div>
        <div style={{ height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={d?.rows || []}>
              <CartesianGrid strokeDasharray="3 3" stroke={BRAND.line} />
              <XAxis dataKey="period" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip formatter={(v, n) => (n === "occupancy_pct" ? `${v}%` : inr(v))} />
              <Bar dataKey="revenue_inr" name="Revenue" fill={BRAND.green} radius={[6, 6, 0, 0]} />
              <Bar dataKey="tax_inr" name="Tax" fill={BRAND.orange} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Chips title="Bookings by source" data={d?.sources} tid="report-sources" />
        <Chips title="Assist requests" data={d?.assists} tid="report-assists" />
      </div>
      <ReportTable rows={d?.rows || []} />
    </div>
  );
}

function ReportTable({ rows }) {
  return (
    <div className="rounded-2xl border-2 bg-white p-4 overflow-x-auto" style={{ borderColor: BRAND.line }} data-testid="report-table">
      <table className="w-full text-xs">
        <thead><tr className="text-left" style={{ color: BRAND.muted }}>{["Period", "Bookings", "Revenue", "Tax", "Commission", "Occupancy", "Ledger in", "Ledger out"].map((h) => <th key={h} className="py-1.5 pr-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.period} className="border-t" style={{ borderColor: BRAND.line }} data-testid={`report-row-${r.period}`}>
              <td className="py-1.5 pr-3 font-bold">{r.period}</td><td className="pr-3">{r.bookings}</td><td className="pr-3">{inr(r.revenue_inr)}</td><td className="pr-3">{inr(r.tax_inr)}</td>
              <td className="pr-3">{inr(r.commission_inr)}</td><td className="pr-3">{r.occupancy_pct}%</td><td className="pr-3">{inr(r.income_inr)}</td><td className="pr-3">{inr(r.expense_inr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
